import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { DetailTicket } from "../../../../types/models/ticket.model.type";
import { Form, Input, Select } from "antd";
import FaIcon from "../../../../components/global/FaIcon";
import { useGetReasonsCancellationQuery } from "../../../../app/features/sharedTickets/sharedTickets.api";
import { useRejectTicketAtMutation } from "../../../../app/features/sharedTickets/sharedTickets.api";
import NextSpinner from "../../../../components/next/NextSpinner";
import RetryButton from "../../../../components/Buttons/RetryButton";
import EmptySelect from "../../../../components/global/EmptySelect";
import { TicketsRejectFormValues } from "../../../../types/ux/tickets.ux.type";
import { RejectTicketRequestAt } from "../../../../types/request/ticket.request.type";
import { useAuth } from "../../../../hooks/useAuth.hook";
import { RejectTicketResponse } from "../../../../types/response/ticket.response.type";
import { useAppDispatch, useAppSelector } from "../../../../hooks/redux.hook";
import { rxCloseModalTicketRejectAt } from "../../../../app/features/sharedTickets/sharedTickets.slice";
import { rxOpenAlertModal } from "../../../../app/features/common/common.slice";
import { ERROR, SUCCESS } from "../../../../utils/enum/responseStates.enum";
import { Messages } from "../../../../utils/mapping/messages.mapping";
import { Spinner } from "../../../../components/global/Spinner";
import { attendTicketsSelector } from "../../../../app/features/attendTickets/attendTickets.slice";
import { getAttendTickets } from "../../../../app/features/attendTickets/attendTickets.thunk";

declare type Props = {
  isOpen: boolean;
  onCloseModal: VoidFunction;
  ticket?: DetailTicket | undefined;
};

const ModalTicketRejectAt = ({ isOpen, onCloseModal, ticket }: Props) => {
  const dispatch = useAppDispatch();
  const { request } = useAppSelector(attendTicketsSelector);
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [selectOptionReason, setSelectOptionReason] = useState<string | undefined>(undefined);
  const { isFetching, data, error, refetch } = useGetReasonsCancellationQuery();
  const [rejectTicketAt, { isLoading: isLoadingRejectTicketAt }] = useRejectTicketAtMutation();

  const onFinish = async (values: TicketsRejectFormValues) => {
    const rejectTicketRequestAt: RejectTicketRequestAt = {
      idTicket: `${ticket!.periodo}-${ticket!.idTicket}`,
      idDet: ticket!.idDetTicket,
      idTema: ticket!.idTema,
      temaDesc: ticket!.temaDesc,
      idSis: ticket!.idSis,
      sistemaDesc: ticket?.sistemaDesc!,
      idinc: ticket!.idInc,
      incidenciaDesc: ticket?.incidenciaDesc!,
      idMotivo: values.reason.split("@")[0],
      motivo: values.reason.split("@")[1],
      respuesta: values.comment,
      isEnvioMail: ticket!.isEnvioMail,
      correoUsuario: ticket!.correoUsuario,
      idUsuario: user!.id.toString(),
      responsable: user!.username,
    };

    const rejectTicketResponse: RejectTicketResponse = await rejectTicketAt(rejectTicketRequestAt).unwrap();
    if (rejectTicketResponse.isSuccess) {
      dispatch(rxCloseModalTicketRejectAt());
      dispatch(
        rxOpenAlertModal({
          title: Messages.empty,
          message: rejectTicketResponse.errorMessage,
          type: SUCCESS,
          callbackOk: () => {}
        })
      );
      dispatch(getAttendTickets(request!));
    } else {
      dispatch(rxCloseModalTicketRejectAt());
      dispatch(
        rxOpenAlertModal({
          title: Messages.empty,
          message: Messages.rejectTicketError,
          type: ERROR,
          callbackOk: () => {},
        })
      );
    }
  };

  const onChangeSelectReason = () => {
    setSelectOptionReason(form.getFieldValue("reason"));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                  Rechazar Ticket
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{ticket?.incidenciaDesc}</p>
                </div>
                <Form className="mt-2" form={form} onFinish={onFinish} layout="vertical" requiredMark="optional">
                  <Form.Item name="reason" label="Seleccione Motivo" required>
                    <Select
                      onChange={onChangeSelectReason}
                      showSearch
                      placeholder="Seleccione"
                      loading={isFetching}
                      notFoundContent={
                        <NextSpinner spinning={isFetching}>
                          {error && (
                            <div className="border border-dashed border-red-300 text-red-500 p-4 rounded-md">
                              Ocurrió un error al cargar los motivos de rechazo.
                              <div className="mt-2">
                                <RetryButton onClick={refetch} />
                              </div>
                            </div>
                          )}
                          {!error && data?.length === 0 && <EmptySelect />}
                        </NextSpinner>
                      }
                      suffixIcon={<FaIcon icon="search" classes="mr-0" />}
                      optionFilterProp="children"
                      allowClear
                    >
                      {data?.map((record) => (
                        <Select.Option key={record.codigo} data={record}>
                          {record.descripcion}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="comment" label="Ingrese Comentario">
                    <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                  </Form.Item>
                  <div className="mt-4 flex justify-end">
                    <button
                      type="submit"
                      disabled={!selectOptionReason}
                      className="mr-5 inline-flex justify-center items-center rounded-md border border-transparent bg-green-100 px-4 py-2 text-sm font-medium text-green-900 hover:bg-green-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                      {isLoadingRejectTicketAt && (
                        <div className="space-x-2 flex items-center">
                          <Spinner />
                          <span>Procesando...</span>
                        </div>
                      )}
                      {!isLoadingRejectTicketAt && (
                        <div>
                          <i className="far fa-check mr-2 fa-fw" />
                          Rechazar
                        </div>
                      )}
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center items-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={onCloseModal}
                    >
                      <i className="far fa-xmark mr-2 fa-fw" />
                      Cancelar
                    </button>
                  </div>
                </Form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ModalTicketRejectAt;
